/**
 * RCD — Retail CD Management dashboard.
 *
 * Surfaces:
 *   - KPI tiles: CDs under management, maturing 30d / 60d / 90d, grace-period-active.
 *   - Maturing-soon grid (30/60/90 window filter) with drill to detail.
 *   - Recently matured (last 30 days).
 *   - List by branch.
 *
 * Data wiring:
 *   - `/accounts/acct-inq/{acctId}/30` for per-CD shape (x_TimeDep* extended).
 *   - `/v3/config/cd-penalty-rules` — loaded via ConfigContext.
 *
 * The dashboard is READ-ONLY. No FSR action is required on maturity —
 * Core Director auto-renews (per modules digest §RCD build guidance).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  KpiTile,
  DataGrid,
  FilterChips,
  GlobalCustomerSearch,
  Pill,
  WidgetCard,
  EntityIcon,
} from '@/components/primitives';
import type { CustomerSearchRow, DataGridColumn } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import { formatMoney, formatShortDate, dayDiff, isWithinDays } from './utils/cdFormat';
import './rcd.css';

export interface CdSummaryRow {
  acctId: string;
  acctType: '30';
  productCode: string;
  productDescription: string;
  customerName: string;
  cifId: string;
  branchCode: string;
  balance: number;
  rateApr: number;
  termMonths: number;
  openDate: string;
  maturityDate: string;
  autoRenew: boolean;
  graceActive: boolean;
  graceDaysRemaining?: number;
}

/**
 * TODO (Wave 3): replace the fixture with `apiClient.retail.config.cdPenaltyRules()` +
 * per-branch enumeration of active CDs via a new `/v3/cds/by-branch/{branchCode}`
 * route. For Wave 2 we ship the UI surface with zero-state rendering + a small
 * local fixture gated behind `import.meta.env.DEV`.
 */
const DEV_FIXTURE_CDS: ReadonlyArray<CdSummaryRow> = import.meta.env.DEV
  ? [
      {
        acctId: '1001234567',
        acctType: '30',
        productCode: 'CD12',
        productDescription: '12 Month CD',
        customerName: 'Smith, John',
        cifId: 'CIF-0042',
        branchCode: '1',
        balance: 25000,
        rateApr: 4.5,
        termMonths: 12,
        openDate: '2025-05-01',
        maturityDate: '2026-05-01',
        autoRenew: true,
        graceActive: false,
      },
      {
        acctId: '1001234568',
        acctType: '30',
        productCode: 'CD24',
        productDescription: '24 Month CD',
        customerName: 'Johnson, Alice',
        cifId: 'CIF-0077',
        branchCode: '1',
        balance: 50000,
        rateApr: 4.75,
        termMonths: 24,
        openDate: '2024-04-25',
        maturityDate: '2026-04-25',
        autoRenew: true,
        graceActive: true,
        graceDaysRemaining: 6,
      },
      {
        acctId: '1001234569',
        acctType: '30',
        productCode: 'CD06',
        productDescription: '6 Month CD',
        customerName: 'Patel, Rani',
        cifId: 'CIF-0181',
        branchCode: '2',
        balance: 10000,
        rateApr: 4.1,
        termMonths: 6,
        openDate: '2025-11-15',
        maturityDate: '2026-05-15',
        autoRenew: false,
        graceActive: false,
      },
    ]
  : [];

type MaturityWindow = 'all' | '30' | '60' | '90';

export default function RCD(): JSX.Element {
  const { config, state } = useConfig();
  const navigate = useNavigate();
  const [window, setWindow] = useState<MaturityWindow>('30');
  const [branch, setBranch] = useState<string>('all');

  const filtered = useMemo<ReadonlyArray<CdSummaryRow>>(() => {
    let rows = DEV_FIXTURE_CDS;
    if (branch !== 'all') rows = rows.filter((r) => r.branchCode === branch);
    if (window !== 'all') {
      const windowDays = Number(window);
      rows = rows.filter((r) => isWithinDays(r.maturityDate, windowDays));
    }
    return rows;
  }, [window, branch]);

  const kpis = useMemo(() => {
    const maturing30 = DEV_FIXTURE_CDS.filter((r) => isWithinDays(r.maturityDate, 30)).length;
    const maturing60 = DEV_FIXTURE_CDS.filter((r) => isWithinDays(r.maturityDate, 60)).length;
    const maturing90 = DEV_FIXTURE_CDS.filter((r) => isWithinDays(r.maturityDate, 90)).length;
    const graceActive = DEV_FIXTURE_CDS.filter((r) => r.graceActive).length;
    return { total: DEV_FIXTURE_CDS.length, maturing30, maturing60, maturing90, graceActive };
  }, []);

  const branchOptions = useMemo(
    () => [
      { value: 'all', label: 'All branches' },
      ...config.branches.map((b) => ({ value: b.branchCode, label: b.branchName })),
    ],
    [config.branches],
  );

  const columns: DataGridColumn<CdSummaryRow>[] = [
    {
      key: 'acct',
      label: 'Account',
      render: (r) => (
        <span className="rcd-cell--acct">
          <EntityIcon type="deposit" code="CD" size={22} />
          <span>
            <strong>{r.acctId}</strong>
            <div className="rcd-cell__sub">{r.productDescription}</div>
          </span>
        </span>
      ),
    },
    { key: 'cust', label: 'Customer', render: (r) => r.customerName },
    { key: 'bal', label: 'Balance', numeric: true, render: (r) => formatMoney(r.balance) },
    { key: 'apr', label: 'APR', numeric: true, render: (r) => `${r.rateApr.toFixed(2)}%` },
    {
      key: 'maturity',
      label: 'Maturity',
      render: (r) => {
        const days = dayDiff(r.maturityDate);
        return (
          <span className="rcd-cell--maturity">
            <span>{formatShortDate(r.maturityDate)}</span>
            <span className="rcd-cell__sub">
              {days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? 'today' : `in ${days}d`}
            </span>
          </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => {
        if (r.graceActive) {
          return <Pill variant="warn">Grace · {r.graceDaysRemaining ?? 0}d left</Pill>;
        }
        if (isWithinDays(r.maturityDate, 30)) return <Pill variant="warn">Maturing soon</Pill>;
        return <Pill variant="ok">Active</Pill>;
      },
    },
    {
      key: 'renew',
      label: 'Renewal',
      render: (r) =>
        r.autoRenew ? <Pill variant="info">Auto-renew</Pill> : <Pill variant="muted">Manual</Pill>,
    },
  ];

  return (
    <div className="rcd-page">
      <header className="rcd-page__header">
        <div className="rcd-page__title-row">
          <h1>RCD — CD Management</h1>
          <Pill variant="muted">Module RCD</Pill>
          {state === 'initializing' && <Pill variant="warn">System initializing</Pill>}
        </div>
        <p className="module-landing__scope">
          Track maturing CDs, review grace-period activity, and launch penalty waivers or
          lost-CD indemnities. Core Director handles auto-renewal; RCD is awareness-first.
        </p>
      </header>

      <WidgetCard title="Find a CD by customer" stripe="pipeline">
        <GlobalCustomerSearch
          filterAcctType="30"
          acctTypeLabel="CD"
          onSelect={(row: CustomerSearchRow) => navigate(`/rcm/${row.cifId}`)}
          placeholder="SSN, name, or CIF of the CD owner"
        />
      </WidgetCard>

      <section className="rcd-kpi-grid">
        <KpiTile label="CDs under management" value={kpis.total} />
        <KpiTile
          label="Maturing in 30 days"
          value={kpis.maturing30}
          severity={kpis.maturing30 > 0 ? 'warn' : 'default'}
        />
        <KpiTile label="Maturing in 60 days" value={kpis.maturing60} />
        <KpiTile label="Maturing in 90 days" value={kpis.maturing90} />
        <KpiTile
          label="Grace period active"
          value={kpis.graceActive}
          severity={kpis.graceActive > 0 ? 'urgent' : 'default'}
        />
      </section>

      <WidgetCard title="Maturing CDs" stripe="pipeline">
        <div className="rcd-toolbar">
          <FilterChips<MaturityWindow>
            label="Maturity window"
            options={[
              { value: '30', label: '30 days' },
              { value: '60', label: '60 days' },
              { value: '90', label: '90 days' },
              { value: 'all', label: 'All' },
            ]}
            selected={window}
            onChange={setWindow}
          />
          <FilterChips
            label="Branch"
            options={branchOptions}
            selected={branch}
            onChange={setBranch}
          />
        </div>
        <DataGrid<CdSummaryRow>
          columns={columns}
          rows={[...filtered]}
          rowKey={(r) => r.acctId}
          onRowAction={(r) => navigate(`cd/${r.acctId}`)}
          emptyMessage="No CDs match the selected window and branch. When SQL-backed config is live, this grid paginates server-side."
        />
      </WidgetCard>

      <div className="rcd-two-col">
        <WidgetCard title="Recently matured (last 30 days)" stripe="queue">
          <div className="rcd-muted-block">
            Wired to `/accounts/acct-inq` post-SQL-migration (Wave 3). Read-only; no FSR action.
          </div>
        </WidgetCard>
        <WidgetCard title="Penalty waivers this month" stripe="approvals">
          <div className="rcd-muted-block">
            Waiver ledger view (reads `pcb-retail-ops.CdPenaltyWaiverReasons`). Wave 3 when
            `/v3/cd-waivers` route lands.
          </div>
        </WidgetCard>
      </div>

      <div className="module-landing__diag">
        <h3>Diagnostics · Config dependencies</h3>
        <div className="module-landing__diag-grid">
          <div className="module-landing__diag-cell">
            <div className="module-landing__diag-cell-label">CD penalty rules</div>
            <div className="module-landing__diag-cell-value">{config.cdPenaltyRules.length}</div>
          </div>
          <div className="module-landing__diag-cell">
            <div className="module-landing__diag-cell-label">Retention policies</div>
            <div className="module-landing__diag-cell-value">{config.retentionPolicies.length}</div>
          </div>
          <div className="module-landing__diag-cell">
            <div className="module-landing__diag-cell-label">Branches loaded</div>
            <div className="module-landing__diag-cell-value">{config.branches.length}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
